import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { EllipsisMenu } from './EllipsisMenu.js'
import { kindNamed } from './fieldKinds.js'
import { MenuItem, MenuRule } from './Menu.js'

/**
 * What can be done to one column, from its own heading.
 *
 * On hover and whenever it has focus, so it is reachable from a keyboard — a
 * control that only appears under a pointer is one nobody tabbing can find.
 *
 * **One menu for both boards.** Contacts and segments each had their own, and
 * they disagreed: a segment's offered neither ordering nor hiding, so the same
 * column heading answered "what can I do to this" differently depending on
 * which board you were standing on. What a column allows differs, and that is
 * what the absent handlers say; *which* things a column can allow does not.
 *
 * **Every item is offered by a handler being present, never by a kind.** The
 * menu cannot see whether a column is one of the board's own or one the
 * organisation declared, and it should not: renaming a base contacts column
 * works because `contact_column_labels` stores the override, and a base
 * segments column cannot be renamed because no such table exists for it. That
 * is knowledge about a board, so each board keeps it.
 *
 * **Renaming a column cannot change who a campaign reaches.** A segment rule
 * names a *field*, never a label — the separation `contact_fields` has had
 * since it existed. Deleting is the one action somebody would reasonably expect
 * to break a saved segment, and it does not: the rule reads the values rather
 * than the declaration, which is why deleting says so before it happens.
 */
export function ColumnMenu({
  label,
  kind,
  sorted,
  onSort,
  onHide,
  onRename,
  onRetype,
  onDelete,
}: {
  readonly label: string
  /** What the column holds now, for a declared one. Absent for a base column. */
  readonly kind: ContactFieldDto['type'] | undefined
  readonly sorted: 'asc' | 'desc' | undefined
  /**
   * Orders the rows by this column, or clears the order.
   *
   * A direction rather than a sort, because the two boards do not spell one the
   * same way: a contact sort names a declared field's key as well as a column,
   * a segment sort is a column id and nothing else. Translating it is the
   * caller's, so this menu never learns either shape.
   *
   * Absent where the column cannot be ordered by — prose rendered from a rule
   * tree, a list per row, a join. A control that did nothing would be worse
   * than none.
   */
  readonly onSort: ((direction: 'asc' | 'desc' | undefined) => void) | undefined
  /** Absent for a column that is always shown: it is the way back to the row. */
  readonly onHide: (() => void) | undefined
  /** Absent where the board has nowhere to store a label of its own. */
  readonly onRename: (() => void) | undefined
  /** Absent for a base column, whose kind is its table's own. */
  readonly onRetype: (() => void) | undefined
  /** Absent for a base column: the data would still be there. */
  readonly onDelete: (() => void) | undefined
}) {
  return (
    <EllipsisMenu hover="head" label={`Options for ${label}`}>
      {(choose) => (
        <>
          <Sorting choose={choose} onSort={onSort} sorted={sorted} />

          {onRename === undefined ? null : <MenuItem onClick={choose(onRename)}>Rename</MenuItem>}

          {/* The kind it is now, because "change type" without it is a question
              somebody has to open the dialog to answer. */}
          {onRetype === undefined ? null : (
            <MenuItem onClick={choose(onRetype)}>
              Change type
              {kind === undefined ? null : <span className="text-t3">{kindNamed(kind)}</span>}
            </MenuItem>
          )}

          {onHide === undefined ? null : (
            <MenuItem onClick={choose(onHide)}>Hide this column</MenuItem>
          )}

          {onDelete === undefined ? null : (
            <>
              <MenuRule />
              <MenuItem danger onClick={choose(onDelete)}>
                Delete column
              </MenuItem>
            </>
          )}
        </>
      )}
    </EllipsisMenu>
  )
}

/**
 * Ordering by this column, where it can be ordered by.
 *
 * Its own component because the menu that holds it reads as a list of what can
 * be done, and three conditional items inline made that list something to work
 * out rather than read — which Biome's complexity rule is for.
 */
function Sorting({
  sorted,
  choose,
  onSort,
}: {
  readonly sorted: 'asc' | 'desc' | undefined
  readonly choose: (act: () => void) => () => void
  readonly onSort: ((direction: 'asc' | 'desc' | undefined) => void) | undefined
}) {
  if (onSort === undefined) return null

  return (
    <>
      <MenuItem chosen={sorted === 'asc'} onClick={choose(() => onSort('asc'))}>
        Sort A → Z
      </MenuItem>
      <MenuItem chosen={sorted === 'desc'} onClick={choose(() => onSort('desc'))}>
        Sort Z → A
      </MenuItem>
      {sorted === undefined ? null : (
        <MenuItem onClick={choose(() => onSort(undefined))}>Clear sorting</MenuItem>
      )}
      <MenuRule />
    </>
  )
}
